/**
 * Edit colors: assign a palette colour to each component of the reproduction.
 *
 * Clicking a component in the preview selects it; picking a palette colour
 * records the assignment. The palette is laid out the way the marked palette
 * plate is, so a colour chosen on screen can be found again on the plate.
 *
 * The tooltip carries the design-space parameters and marking time behind each
 * colour, because two visually similar palette entries can cost very different
 * amounts of laser time.
 *
 * Two dialogs act on the whole design: reuse the assignment of another logo
 * reproduction on the same palette, or deal a few chosen palette colours out
 * at random.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack, Typography, Box, Alert, Checkbox, FormControlLabel,
  CircularProgress, TextField, Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import LayersClearIcon from "@mui/icons-material/LayersClear";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import PaletteSwatchGrid from "../../components/PaletteSwatchGrid";
import AssignmentReuseDialog from "../../components/AssignmentReuseDialog";
import RandomAssignmentDialog from "../../components/RandomAssignmentDialog";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import PreviewToggle from "../../components/PreviewToggle";
import FinalizeDialog from "../../components/FinalizeDialog";
import { displayedToNaturalImageCoords } from "../../helper/imageOverlayCoordinates";
import { reproductionSidebar } from "../../helper/reproductionFlow";
import { useReproductionParams } from "../../helper/entityRoutes";

const PREVIEW_TOGGLE_OPTIONS = [
  { value: "original", label: "Original" },
  { value: "reproduction", label: "Reproduction preview" },
];

// Labels of the component types other than SHAPE.
const COMPONENT_TYPE_LABELS = { LINE: "Line", LAYER: "Color layer" };

// Clusters per line of a component as the input shows it.
const stackField = (component) => String(component?.cluster_stack_count ?? 1);
const STACK_ERROR = "Clusters per line must be a whole number of at least 1.";

const imagePanelSx = {
  position: "relative",
  width: "100%",
  borderRadius: 2,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
};

// Step 2: assign palette colors to the parsed logo components. One large
// preview panel toggles between the original colors and the palette-mapped
// reproduction; clicking identifies a component in either view. Next
// makes the assignment final: the backend stamps the reproduction, the
// Marking step is the only step left (a final reproduction lands there).
export default function ColorReproductionEdit() {
  const navigate = useNavigate();
  const { reproductionId, reproductionType: urlType, stepRoute } = useReproductionParams();

  const [details, setDetails] = useState(null);
  const [palette, setPalette] = useState([]);
  // Columns of the marked palette plate: the swatches are laid out like it.
  const [paletteColumns, setPaletteColumns] = useState(null);
  const [components, setComponents] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const [originalUrl, setOriginalUrl] = useState("");
  const [reproductionUrl, setReproductionUrl] = useState("");
  const [maskUrl, setMaskUrl] = useState("");
  const originalUrlRef = useRef("");
  const reproductionUrlRef = useRef("");
  const maskUrlRef = useRef("");

  const [selectedComponent, setSelectedComponent] = useState(null);
  const [previewView, setPreviewView] = useState("original");
  const [applyToSameColor, setApplyToSameColor] = useState(false);
  // Line clusters mode: clusters per line of the selected component's assignment.
  const [stackInput, setStackInput] = useState("1");
  const [identifying, setIdentifying] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [reuseDialogOpen, setReuseDialogOpen] = useState(false);
  const [randomDialogOpen, setRandomDialogOpen] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [automaticDialogOpen, setAutomaticDialogOpen] = useState(false);
  // A whole-design tool that runs without its own dialog (unassign all, assign automatically).
  const [toolRunning, setToolRunning] = useState(false);
  // What a whole-design tool did, shown until dismissed.
  const [noticeMessage, setNoticeMessage] = useState(null);
  const previewImageRef = useRef(null);

  const rasterMode = (details?.logo?.reproduction_mode || details?.reproduction_mode) === "RASTER_SCANLINES";
  const lineMode = (details?.logo?.reproduction_mode || details?.reproduction_mode) === "LINE_CLUSTERS";
  const effectiveApplyToSame = rasterMode ? true : applyToSameColor;
  const stackValid = /^\d+$/.test(stackInput.trim()) && Number(stackInput) >= 1;
  const shownPreviewUrl = previewView === "original" ? originalUrl : reproductionUrl;

  const replaceUrl = (ref, setter, blob) => {
    if (ref.current) URL.revokeObjectURL(ref.current);
    const url = blob ? URL.createObjectURL(blob) : "";
    ref.current = url;
    setter(url);
  };

  const fetchComponents = async () => {
    const response = await axios.post("api/reproduction/get_components", {
      reproduction_id: reproductionId,
    });
    const rows = response.data.components || [];
    setComponents(rows);
    return rows;
  };

  const fetchReproductionPreview = async () => {
    const response = await axios.post(
      "api/reproduction/get_reproduction_image_preview",
      { reproduction_id: reproductionId },
      { responseType: "blob" },
    );
    replaceUrl(reproductionUrlRef, setReproductionUrl, response.data);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const detailsResponse = await axios.post("api/reproduction/get_reproduction_details", {
          reproduction_id: reproductionId,
        });
        // The edit step does not exist for image reproductions: the
        // halftoning assigns the palette colors itself.
        const type = detailsResponse.data.reproduction_type || urlType;
        if (type === "IMAGE_REPRODUCTION") {
          navigate(stepRoute("gamut", type), { replace: true });
          return;
        }
        if (detailsResponse.data.setup_final) {
          // The assignment is final: only the Marking step is open.
          navigate(stepRoute("marking", type), { replace: true });
          return;
        }
        setDetails(detailsResponse.data);
        const paletteResponse = await axios.post("api/reproduction/get_palette", {
          reproduction_id: reproductionId,
        });
        setPalette(paletteResponse.data.palette || []);
        setPaletteColumns(paletteResponse.data.grid?.columns || null);
        await fetchComponents();
        const inputResponse = await axios.post(
          "api/reproduction/get_input_image_preview",
          { reproduction_id: reproductionId },
          { responseType: "blob" },
        );
        replaceUrl(originalUrlRef, setOriginalUrl, inputResponse.data);
        await fetchReproductionPreview();
      } catch (error) {
        console.error("Error loading the reproduction:", error);
        setErrorMessage("Loading the reproduction failed.");
      }
    };
    load();
    return () => {
      // Release every object URL when leaving the page.
      [originalUrlRef, reproductionUrlRef, maskUrlRef].forEach((ref) => {
        if (ref.current) URL.revokeObjectURL(ref.current);
      });
    };
  }, []);

  const fetchMaskForComponent = async (componentId) => {
    try {
      const response = await axios.post(
        "api/reproduction/get_highlighted_component_mask",
        { reproduction_id: reproductionId, component_id: componentId },
        { responseType: "blob" },
      );
      replaceUrl(maskUrlRef, setMaskUrl, response.data);
    } catch (error) {
      console.error("Error loading the component highlight:", error);
      replaceUrl(maskUrlRef, setMaskUrl, null);
    }
  };

  const selectComponent = async (component, { forceApplyToSame = false } = {}) => {
    setSelectedComponent(component);
    setStackInput(stackField(component));
    if (forceApplyToSame) setApplyToSameColor(true);
    await fetchMaskForComponent(component.component_id);
  };

  const handlePreviewClick = async (event) => {
    const image = previewImageRef.current;
    if (!image || !image.naturalWidth || identifying) return;
    const imageRect = image.getBoundingClientRect();
    const { x: xNatural, y: yNatural } = displayedToNaturalImageCoords(
      image, event.clientX - imageRect.left, event.clientY - imageRect.top,
    );
    // Normalize to [0, 1] of the natural image size.
    const x = Math.min(Math.max(xNatural / image.naturalWidth, 0), 1);
    const y = Math.min(Math.max(yNatural / image.naturalHeight, 0), 1);
    setIdentifying(true);
    setErrorMessage(null);
    try {
      const response = await axios.post("api/reproduction/identify_clicked_component", {
        reproduction_id: reproductionId, x, y,
      });
      if (response.data && response.data.component_id != null) {
        await selectComponent(response.data);
      } else {
        setSelectedComponent(null);
        replaceUrl(maskUrlRef, setMaskUrl, null);
      }
    } catch (error) {
      console.error("Error identifying the clicked component:", error);
      setErrorMessage("Identifying the clicked component failed.");
    } finally {
      setIdentifying(false);
    }
  };

  // Stores fields of the selected component's assignment (and, with the
  // same-color box, of every component of its source color).
  const saveAssignment = async (fields) => {
    if (!selectedComponent || assigning) return;
    setAssigning(true);
    setErrorMessage(null);
    try {
      await axios.post("api/reproduction/update_component", {
        reproduction_id: reproductionId,
        component_id: selectedComponent.component_id,
        ...fields,
        apply_to_same_color: effectiveApplyToSame,
      });
      const rows = await fetchComponents();
      const updated = rows.find((row) => row.component_id === selectedComponent.component_id);
      if (updated) {
        setSelectedComponent(updated);
        setStackInput(stackField(updated));
      }
      await fetchReproductionPreview();
    } catch (error) {
      console.error("Error assigning the palette color:", error);
      const detail = error?.response?.data?.error;
      setErrorMessage(detail || "Assigning the palette color failed.");
    } finally {
      setAssigning(false);
    }
  };

  // Palette entries are addressed by the id of their palette sample, never
  // by their position - a re-composed palette cannot shift an assignment.
  // In line clusters mode the clusters per line go with the color.
  const assignColor = (sampleId) => {
    if (lineMode && !stackValid) {
      setErrorMessage(STACK_ERROR);
      return;
    }
    saveAssignment({
      sample_id: sampleId,
      ...(lineMode ? { cluster_stack_count: Number(stackInput) } : {}),
    });
  };

  // "Do not mark": the component (with the same-color box, its whole source
  // color) is left out of the marking; picking a palette color takes it back in.
  const excludeFromMarking = () => saveAssignment({ excluded_from_marking: true });

  // A changed clusters per line is stored when the input is left (or Enter).
  const commitStack = () => {
    if (!lineMode || !selectedComponent) return;
    if (!stackValid) {
      setErrorMessage(STACK_ERROR);
      return;
    }
    if (Number(stackInput) === Number(stackField(selectedComponent))) return;
    saveAssignment({ cluster_stack_count: Number(stackInput) });
  };

  // After a whole-design change (assignment taken over, colors dealt out)
  // the components, the selected one and the preview are read again.
  const refreshAssignment = async () => {
    try {
      const rows = await fetchComponents();
      if (selectedComponent) {
        const updated = rows.find((row) => row.component_id === selectedComponent.component_id);
        if (updated) {
          setSelectedComponent(updated);
          setStackInput(stackField(updated));
        }
      }
      await fetchReproductionPreview();
    } catch (error) {
      console.error("Error reloading the assignment:", error);
      setErrorMessage("Reloading the assignment failed.");
    }
  };

  const onAssignmentReused = async (result) => {
    const how = result.matching === "components"
      ? "component by component"
      : (result.matching === "positions"
        ? `${result.matched_by_position} by position, the rest by source color`
        : "by source color");
    const unmatched = (result.color_count ?? 0) - (result.matched_colors ?? 0);
    const kept = unmatched === 1
      ? " 1 source color has no match there and keeps the current color."
      : ` ${unmatched} source colors have no match there and keep the current color.`;
    setNoticeMessage(
      `Assignment of "${result.source_name}" applied to ${result.components_updated} of `
      + `${result.component_count} components (${how}).${unmatched > 0 ? kept : ""}`,
    );
    await refreshAssignment();
  };

  const onRandomAssigned = async (result) => {
    const colors = result.colors_used === 1 ? "1 palette color" : `${result.colors_used} palette colors`;
    const targets = result.components_updated === 1 ? "1 component" : `${result.components_updated} components`;
    setNoticeMessage(`${colors} dealt out at random to ${targets}.`);
    await refreshAssignment();
  };

  // The two confirmed whole-design tools: every component set to "Do not
  // mark", or the set-up's automatic choice made again for every
  // component (any "Do not mark" included).
  const runTool = async (url, closeDialog, failure, notice) => {
    setToolRunning(true);
    setErrorMessage(null);
    try {
      const response = await axios.post(url, { reproduction_id: reproductionId });
      closeDialog();
      setNoticeMessage(notice(response.data));
      await refreshAssignment();
    } catch (error) {
      console.error(failure, error);
      closeDialog();
      setErrorMessage(error?.response?.data?.error || failure);
    } finally {
      setToolRunning(false);
    }
  };

  const unassignAll = () => runTool(
    "api/reproduction/assignment/clear",
    () => setUnassignDialogOpen(false),
    "Setting the components to Do not mark failed.",
    (result) => (result.components_cleared === 1
      ? "1 component set to Do not mark."
      : `${result.components_cleared} components set to Do not mark.`),
  );

  const reassignAutomatically = () => runTool(
    "api/reproduction/assignment/automatic",
    () => setAutomaticDialogOpen(false),
    "Assigning the palette colors automatically failed.",
    (result) => (result.components_assigned === 1
      ? "1 component was assigned its closest palette color."
      : `${result.components_assigned} components were assigned their closest palette color.`),
  );

  // Next asks for confirmation, then fixes the assignment for good: the
  // backend stamps the reproduction final and refuses every later change.
  const finalizeReproduction = async () => {
    setFinalizing(true);
    setErrorMessage(null);
    try {
      await axios.post("api/reproduction/finalize", { reproduction_id: reproductionId });
      setFinalizeDialogOpen(false);
      navigate(stepRoute("marking", details?.reproduction_type));
    } catch (error) {
      console.error("Error finalizing the reproduction:", error);
      const detail = error?.response?.data?.error;
      setFinalizeDialogOpen(false);
      setErrorMessage(detail || "Finalizing the reproduction failed.");
    } finally {
      setFinalizing(false);
    }
  };

  // Components grouped by their source color.
  const colorGroups = [];
  components.forEach((component) => {
    const group = colorGroups.find((entry) => entry.hex === component.target_color_hex);
    if (group) {
      group.members.push(component);
    } else {
      colorGroups.push({ hex: component.target_color_hex, members: [component] });
    }
  });

  const unassignedCount = components.filter(
    (component) => !component.palette_sample_id && !component.excluded_from_marking,
  ).length;
  const excludedCount = components.filter((component) => component.excluded_from_marking).length;
  const assignedCount = components.length - unassignedCount - excludedCount;
  // The marked palette plate's columns (fallback: the most-square grid).
  const gridColumns = paletteColumns || Math.max(1, Math.ceil(Math.sqrt(palette.length || 1)));
  const paletteBySample = new Map(palette.map((entry) => [entry.sample_id, entry]));
  const assignedEntry = selectedComponent
    ? paletteBySample.get(selectedComponent.palette_sample_id) || null
    : null;

  const componentTypeLabel = selectedComponent
    ? (COMPONENT_TYPE_LABELS[selectedComponent.component_type] || "Shape")
    : null;
  // A color layer (rasterized scanline mode) stands for every SVG object
  // of its color.
  const objectCount = (members) => members.reduce(
    (count, member) => count + (member.component_type === "LAYER" ? (member.source_object_count ?? 1) : 1),
    0,
  );
  const checkboxLabel = selectedComponent && selectedComponent.component_type === "LINE"
    ? "Apply to all lines with this color"
    : "Apply to all objects with this color";

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="Edit colors"
        subtitle="Click a component in the logo, then pick the palette color it should be marked with."
        stepSidebar={reproductionSidebar(1, components.length || null)}
        footerBelowPaper={
          <Box sx={{ width: "100%" }}>
            <NavBarBottom
              variant="embedded"
              returnLabel="Back"
              prevPage={stepRoute("setup", "LOGO_REPRODUCTION")}
              nextPage={stepRoute("marking", "LOGO_REPRODUCTION")}
              enableNextPage={components.length > 0 && assignedCount === 0}
              nextLoading={finalizing}
              nextPageClickFunc={() => setFinalizeDialogOpen(true)}
            />
          </Box>
        }
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          {noticeMessage && (
            <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setNoticeMessage(null)}>
              {noticeMessage}
            </Alert>
          )}
          {unassignedCount > 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {unassignedCount === 1
                ? "1 component has no palette color yet — it will be skipped in the marking file."
                : `${unassignedCount} components have no palette color yet — they will be skipped in the marking file.`}
            </Alert>
          )}

          {/* Preview on the left, color assignment on the right. The toggle
              switches the panel between the original colors and the
              palette-mapped reproduction; both views share the same canvas
              geometry, so clicking works in either. */}
          <Box
            sx={{
              display: "grid",
              gap: 2.5,
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 360px" },
              alignItems: "start",
            }}
          >
          <Box>
            <Box
              sx={{
                ...imagePanelSx,
                aspectRatio: String(details?.source_aspect_ratio || 1),
                maxHeight: "68vh",
                mx: "auto",
                cursor: shownPreviewUrl ? "crosshair" : "default",
              }}
            >
              {shownPreviewUrl ? (
                <>
                  <img
                    ref={previewImageRef}
                    src={shownPreviewUrl}
                    alt={previewView === "original" ? "Original logo" : "Reproduction preview"}
                    onClick={handlePreviewClick}
                    style={{
                      position: "absolute", inset: 0, width: "100%", height: "100%",
                      objectFit: "contain", display: "block",
                    }}
                  />
                  {maskUrl ? (
                    <img
                      src={maskUrl}
                      alt="Selected component highlight"
                      style={{
                        position: "absolute", inset: 0, width: "100%", height: "100%",
                        objectFit: "contain", display: "block", pointerEvents: "none",
                      }}
                    />
                  ) : null}
                </>
              ) : (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
                  <Typography variant="caption" color="text.disabled">
                    Preview appears here
                  </Typography>
                </Box>
              )}
            </Box>
            <Stack alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PreviewToggle value={previewView} onChange={setPreviewView} options={PREVIEW_TOGGLE_OPTIONS} />
                {identifying ? (
                  <CircularProgress size={14} thickness={5} aria-label="Identifying component" />
                ) : null}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {rasterMode
                  ? "Click a color to select its layer — in both views."
                  : "Click a shape or line to select it — in both views."}
              </Typography>
            </Stack>
          </Box>

          {/* Right column: assignment panel + color groups. */}
          <Stack spacing={2.5}>
          <Box
            sx={{
              p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Color assignment
            </Typography>
            {/* Whole-design tools, each in its own dialog. */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={assigning || finalizing || components.length === 0}
                onClick={() => setReuseDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
              >
                Reuse assignment
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ShuffleIcon />}
                disabled={assigning || finalizing || components.length === 0}
                onClick={() => setRandomDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
              >
                Assign randomly
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoFixHighIcon />}
                disabled={assigning || finalizing || toolRunning || components.length === 0}
                onClick={() => setAutomaticDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
              >
                Assign automatically
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LayersClearIcon />}
                disabled={assigning || finalizing || toolRunning || components.length === 0}
                onClick={() => setUnassignDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
              >
                Unassign all
              </Button>
            </Stack>
            {selectedComponent ? (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {componentTypeLabel}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Source color</Typography>
                    <Box
                      sx={{
                        width: 20, height: 20, borderRadius: 0.75,
                        bgcolor: selectedComponent.target_color_hex,
                        border: "1px solid rgba(15, 23, 42, 0.2)",
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {assignedEntry
                      ? `Assigned to palette color ${assignedEntry.index}`
                      : selectedComponent.excluded_from_marking
                        ? "Left out of the marking"
                        : "No palette color assigned"}
                  </Typography>
                  {assigning ? (
                    <CircularProgress size={14} thickness={5} aria-label="Assigning color" />
                  ) : null}
                </Stack>

                {rasterMode ? (
                  <Typography variant="caption" color="text.secondary">
                    All objects of this source color form one layer; the assignment applies to all of them.
                  </Typography>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={applyToSameColor}
                        onChange={(event) => setApplyToSameColor(event.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{checkboxLabel}</Typography>}
                  />
                )}

                {lineMode && (
                  <TextField
                    label="Clusters per line"
                    type="number"
                    size="small"
                    value={stackInput}
                    // Not editable while a save runs: a change then would be dropped.
                    disabled={assigning}
                    onChange={(event) => setStackInput(event.target.value)}
                    onBlur={commitStack}
                    onKeyDown={(event) => { if (event.key === "Enter") commitStack(); }}
                    inputProps={{ min: 1, step: 1 }}
                    helperText="Clusters of the assigned color marked side by side on the line."
                    sx={{ maxWidth: 240 }}
                  />
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    Click a palette color to assign it (layout of the marked palette):
                  </Typography>
                  <PaletteSwatchGrid
                    palette={palette}
                    columns={gridColumns}
                    highlighted={(entry) => selectedComponent.palette_sample_id === entry.sample_id}
                    onPick={(entry) => assignColor(entry.sample_id)}
                    label={(entry) => `Assign palette color ${entry.index}`}
                    busy={assigning}
                  />
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    size="small"
                    variant={selectedComponent.excluded_from_marking ? "contained" : "outlined"}
                    disabled={assigning}
                    // Same as the swatches: keep the clusters input's blur from racing the click.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={excludeFromMarking}
                    sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
                  >
                    Do not mark
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Leaves it out of the marking file; pick a color to mark it again.
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No component selected — click the logo preview or a color group below.
              </Typography>
            )}
          </Box>

          {/* Compact color-group list: primary interaction in raster mode. */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Color groups
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Selecting a group assigns the next palette color to every object with
              this source color.
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {colorGroups.map((group) => {
                const selected = selectedComponent
                  && group.members.some((member) => member.component_id === selectedComponent.component_id);
                return (
                  <Box
                    key={group.hex}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectComponent(group.members[0], { forceApplyToSame: true })}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.75,
                      px: 1.25, py: 0.5, borderRadius: 2, cursor: "pointer",
                      border: selected ? "2px solid" : "1px solid rgba(15, 23, 42, 0.15)",
                      borderColor: selected ? "primary.main" : "rgba(15, 23, 42, 0.15)",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        width: 18, height: 18, borderRadius: 0.75,
                        bgcolor: group.hex,
                        border: "1px solid rgba(15, 23, 42, 0.2)",
                      }}
                    />
                    <Typography variant="body2">
                      {`${objectCount(group.members)} object${objectCount(group.members) === 1 ? "" : "s"}`}
                    </Typography>
                    {group.members.every((member) => member.excluded_from_marking) ? (
                      <Typography variant="caption" color="text.secondary">Not marked</Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          </Box>
          </Stack>{/* end right column */}
          </Box>{/* end preview + assignment grid */}
        </Stack>
      </ExplorationPageShell>

      <FinalizeDialog
        open={finalizeDialogOpen}
        onClose={() => setFinalizeDialogOpen(false)}
        onConfirm={finalizeReproduction}
        confirming={finalizing}
        title="Finalize the color assignment?"
        text={`${assignedCount} of ${components.length} components have a palette color${excludedCount > 0 ? `, ${excludedCount} are not marked` : ""}; unassigned components are skipped. The assignment cannot be changed afterwards.`}
      />
      <AssignmentReuseDialog
        open={reuseDialogOpen}
        onClose={() => setReuseDialogOpen(false)}
        reproductionId={reproductionId}
        onApplied={onAssignmentReused}
      />
      <RandomAssignmentDialog
        open={randomDialogOpen}
        onClose={() => setRandomDialogOpen(false)}
        reproductionId={reproductionId}
        palette={palette}
        columns={gridColumns}
        rasterMode={rasterMode}
        excludedCount={excludedCount}
        onApplied={onRandomAssigned}
      />
      <FinalizeDialog
        open={automaticDialogOpen}
        onClose={() => setAutomaticDialogOpen(false)}
        onConfirm={reassignAutomatically}
        confirming={toolRunning}
        title="Assign the palette colors automatically?"
        text={`Every component gets its closest palette color again, as at set-up; the current assignment is replaced.${excludedCount > 0 ? " Components set to Do not mark are taken back in." : ""}`}
        confirmLabel="Assign"
      />
      <FinalizeDialog
        open={unassignDialogOpen}
        onClose={() => setUnassignDialogOpen(false)}
        onConfirm={unassignAll}
        confirming={toolRunning}
        title="Set every component to Do not mark?"
        text="Every component loses its palette color and is left out of the marking. Picking a color for a component, or Assign automatically, takes it back in."
        confirmLabel="Unassign"
      />
    </Stack>
  );
}
